import { useRef, useState } from "react";
import { api, formatCep } from "../../lib/api";
import { Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] focus:border-transparent placeholder:text-slate-400";

export default function AddressForm({ value, onChange, testIdPrefix, showObs = false }) {
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState("");
  const lastCep = useRef("");

  const set = (k, v) => onChange({ ...value, [k]: v });

  const lookupCep = async (cep) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    // Economia de API: não reconsultar CEP já carregado com sucesso
    if (digits === lastCep.current) return;
    setLoadingCep(true);
    setCepError("");
    try {
      const r = await api.get(`/cep/${digits}`);
      lastCep.current = digits;
      onChange({
        ...value,
        cep: formatCep(digits),
        logradouro: r.data.logradouro || value.logradouro,
        bairro: r.data.bairro || value.bairro,
        cidade: r.data.cidade || value.cidade,
        uf: r.data.uf || value.uf,
      });
    } catch (e) {
      const status = e.response?.status;
      setCepError(
        status === 404
          ? "CEP não encontrado. Confira o CEP informado."
          : "Serviço de CEP temporariamente indisponível. Tente novamente."
      );
    } finally {
      setLoadingCep(false);
    }
  };

  const [showBusca, setShowBusca] = useState(false);
  const [busca, setBusca] = useState({ uf: "", cidade: "", bairro: "", rua: "" });
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState(null);
  const [buscaErro, setBuscaErro] = useState("");
  const [buscaAviso, setBuscaAviso] = useState("");
  const consultaKey = useRef("");
  const reqSeq = useRef(0);

  const normTxt = (s) => (s || "").trim().replace(/\s+/g, " ");
  const semAcento = (s) => normTxt(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const chaveAtual = () => `${normTxt(busca.uf).toUpperCase()}|${semAcento(busca.cidade)}|${semAcento(busca.rua)}`;

  const prontoParaBuscar =
    busca.uf.trim().length === 2 && busca.cidade.trim().length >= 3 && busca.rua.trim().length >= 3;

  const preencherComResultado = (r) => {
    lastCep.current = r.cep;
    onChange({
      ...value,
      cep: formatCep(r.cep),
      logradouro: r.logradouro || value.logradouro,
      bairro: r.bairro || value.bairro,
      cidade: r.cidade || value.cidade,
      uf: r.uf || value.uf,
    });
    setShowBusca(false);
    setResultados(null);
    setBuscaErro("");
    setBuscaAviso("");
    toast.success(`CEP ${formatCep(r.cep)} aplicado. Confira os dados e informe o número.`);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testIdPrefix}-numero-input"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
      }
    }, 200);
  };

  const buscarCep = async () => {
    if (!prontoParaBuscar || buscando) return;
    const seq = ++reqSeq.current;
    const chave = chaveAtual();
    setBuscando(true);
    setBuscaErro("");
    setBuscaAviso("");
    try {
      const r = await api.get("/buscar-cep", {
        params: { uf: normTxt(busca.uf), cidade: normTxt(busca.cidade), logradouro: normTxt(busca.rua) },
      });
      if (seq !== reqSeq.current) return; // resposta antiga: usuário alterou os dados
      consultaKey.current = chave;
      const lista = r.data.resultados || [];
      if (lista.length === 1) {
        // Resultado único e confirmado pela fonte: preenche direto os campos oficiais
        setResultados(null);
        preencherComResultado(lista[0]);
      } else {
        setResultados(lista);
      }
    } catch (e) {
      if (seq !== reqSeq.current) return;
      const status = e.response?.status;
      setResultados(null);
      setBuscaErro(
        status === 503
          ? "Serviço de busca de CEP temporariamente indisponível. Tente novamente."
          : status === 400
            ? e.response?.data?.detail || "Revise UF, cidade e rua para buscar."
            : "Não foi possível buscar o CEP agora. Verifique sua conexão e tente novamente."
      );
    } finally {
      if (seq === reqSeq.current) setBuscando(false);
    }
  };

  const onBuscaChange = (k) => (e) => {
    const v = k === "uf" ? e.target.value.toUpperCase() : e.target.value;
    setBusca((b) => ({ ...b, [k]: v }));
    if (consultaKey.current) {
      // UF/cidade/rua mudaram após uma busca: invalida o resultado anterior
      consultaKey.current = "";
      reqSeq.current += 1;
      setResultados(null);
      setBuscaAviso("Endereço alterado — toque em BUSCAR CEP para pesquisar novamente.");
    }
  };

  const toggleBusca = () => {
    if (!showBusca) {
      setBusca({
        uf: value.uf || "",
        cidade: value.cidade || "",
        bairro: value.bairro || "",
        rua: value.logradouro || "",
      });
    }
    setShowBusca((s) => !s);
    setBuscaErro("");
    setBuscaAviso("");
    setResultados(null);
    consultaKey.current = "";
    reqSeq.current += 1;
  };

  const resultadosExibidos =
    resultados && busca.bairro.trim()
      ? (() => {
          const b = semAcento(busca.bairro);
          const filtrados = resultados.filter((r) => semAcento(r.bairro).includes(b));
          return filtrados.length ? filtrados : resultados;
        })()
      : resultados;

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CEP *</label>
        <div className="relative mt-1">
          <input
            data-testid={`${testIdPrefix}-cep-input`}
            inputMode="numeric"
            className={inputCls}
            placeholder="00000-000"
            value={value.cep}
            onChange={(e) => {
              const v = formatCep(e.target.value);
              set("cep", v);
              if (v.replace(/\D/g, "").length === 8) lookupCep(v);
            }}
          />
          {loadingCep && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#F5B301]" />}
        </div>
        {cepError && <p className="text-xs text-red-500 mt-1" data-testid={`${testIdPrefix}-cep-error`}>{cepError}</p>}
      </div>

      <button
        type="button"
        data-testid={`${testIdPrefix}-nao-sei-cep-btn`}
        onClick={toggleBusca}
        className="text-xs font-semibold text-navy/70 underline decoration-[#F5B301] decoration-2 underline-offset-2 active:scale-95 transition-transform"
      >
        Não sei meu CEP? Preencher endereço
      </button>

      {showBusca && (
        <div className="rounded-xl border border-[#F5B301]/50 bg-gold-light/40 p-3 space-y-2.5" data-testid={`${testIdPrefix}-busca-cep-panel`}>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">UF *</label>
              <input data-testid={`${testIdPrefix}-busca-uf-input`} className={`${inputCls} mt-1`} maxLength={2} placeholder="RJ"
                value={busca.uf} onChange={onBuscaChange("uf")} />
            </div>
            <div className="col-span-3">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Cidade *</label>
              <input data-testid={`${testIdPrefix}-busca-cidade-input`} className={`${inputCls} mt-1`} placeholder="Rio de Janeiro"
                value={busca.cidade} onChange={onBuscaChange("cidade")} />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Bairro (ajuda a refinar)</label>
            <input data-testid={`${testIdPrefix}-busca-bairro-input`} className={`${inputCls} mt-1`} placeholder="Centro"
              value={busca.bairro} onChange={onBuscaChange("bairro")} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Rua / Avenida *</label>
            <input data-testid={`${testIdPrefix}-busca-rua-input`} className={`${inputCls} mt-1`} placeholder="Ex.: Rua 1º de Março"
              value={busca.rua} onChange={onBuscaChange("rua")} />
            <p className="text-[10px] text-slate-400 mt-1">Digite apenas o nome da rua — o número você informa no campo Número.</p>
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-buscar-cep-btn`}
            onClick={buscarCep}
            disabled={!prontoParaBuscar || buscando}
            className="w-full py-2.5 rounded-xl bg-navy text-white text-xs font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-40"
          >
            {buscando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {buscando ? "BUSCANDO..." : "BUSCAR CEP"}
          </button>
          {buscaErro && <p className="text-xs text-red-500" data-testid={`${testIdPrefix}-busca-erro`}>{buscaErro}</p>}
          {buscaAviso && <p className="text-xs text-amber-700 font-medium" data-testid={`${testIdPrefix}-busca-aviso`}>{buscaAviso}</p>}
          {resultados && resultados.length === 0 && !buscando && (
            <p className="text-xs text-amber-700 font-medium" data-testid={`${testIdPrefix}-busca-vazio`}>
              Nenhum CEP encontrado para este endereço. Revise os dados (nome da rua sem número, cidade e UF) ou preencha o CEP manualmente acima.
            </p>
          )}
          {resultadosExibidos && resultadosExibidos.length > 0 && (
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid={`${testIdPrefix}-cep-resultados`}>
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Toque no endereço correto:</p>
              {resultadosExibidos.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`${testIdPrefix}-cep-resultado-${i}`}
                  onClick={() => preencherComResultado(r)}
                  className="w-full text-left px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-[#F5B301] active:scale-[0.99] transition-all"
                >
                  <p className="text-xs font-semibold text-navy">{[r.logradouro, r.bairro].filter(Boolean).join(" — ") || "Endereço"}</p>
                  <p className="text-[11px] text-slate-500">{r.cidade}{r.uf ? `/${r.uf}` : ""} · CEP {formatCep(r.cep)}{r.faixa ? ` · ${r.faixa}` : ""}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Número *</label>
          <input
            data-testid={`${testIdPrefix}-numero-input`}
            inputMode="numeric"
            className={`${inputCls} mt-1`}
            placeholder="Nº"
            value={value.numero}
            onChange={(e) => set("numero", e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Complemento</label>
          <input
            data-testid={`${testIdPrefix}-complemento-input`}
            className={`${inputCls} mt-1`}
            placeholder="Apto, bloco, casa..."
            value={value.complemento}
            onChange={(e) => set("complemento", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Referência (opcional)</label>
        <input
          data-testid={`${testIdPrefix}-referencia-input`}
          className={`${inputCls} mt-1`}
          placeholder="Ex.: próximo ao mercado, portão azul..."
          value={value.referencia || ""}
          onChange={(e) => set("referencia", e.target.value)}
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rua</label>
        <input
          data-testid={`${testIdPrefix}-rua-input`}
          className={`${inputCls} mt-1`}
          placeholder="Rua / Avenida"
          value={value.logradouro}
          onChange={(e) => set("logradouro", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bairro</label>
          <input
            data-testid={`${testIdPrefix}-bairro-input`}
            className={`${inputCls} mt-1`}
            value={value.bairro}
            onChange={(e) => set("bairro", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cidade</label>
            <input
              data-testid={`${testIdPrefix}-cidade-input`}
              className={`${inputCls} mt-1`}
              value={value.cidade}
              onChange={(e) => set("cidade", e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">UF</label>
            <input
              data-testid={`${testIdPrefix}-uf-input`}
              className={`${inputCls} mt-1`}
              maxLength={2}
              value={value.uf}
              onChange={(e) => set("uf", e.target.value.toUpperCase())}
            />
          </div>
        </div>
      </div>

      {showObs && (
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Observação</label>
          <input
            data-testid={`${testIdPrefix}-obs-input`}
            className={`${inputCls} mt-1`}
            placeholder="Ex.: retirar caixas na garagem"
            value={value.observacao}
            onChange={(e) => set("observacao", e.target.value)}
          />
        </div>
      )}

      {value.cidade && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`${testIdPrefix}-summary`}>
          <MapPin className="w-3.5 h-3.5 text-[#F5B301]" />
          {[value.logradouro, value.numero, value.bairro, value.cidade && value.uf ? `${value.cidade} - ${value.uf}` : value.cidade]
            .filter(Boolean)
            .join(", ")}
        </div>
      )}
    </div>
  );
}
